import React from 'react';
import { SalvDropdown, Size } from '@salvapp/ui-components';
import { InternalApiEntityGroup, InternalApiScreeningFlow, InternalApiScreeningMatchType } from '../../../api-internal';
import {
  getMatchLabel,
  getSegmentsOptions,
  getSreenedAgainstOptions,
  getSreenedFieldsOptions,
  toDropdownOption,
} from './helper';
import { isDefined } from '../../../common/helper';

export type FlowFilters = {
  screenableFields: string[];
  segments: InternalApiEntityGroup[];
  matchingTypes: InternalApiScreeningMatchType[];
  screenedAgainst: string[];
};

export const emptyFlowFilters: FlowFilters = {
  screenableFields: [],
  segments: [],
  matchingTypes: [],
  screenedAgainst: [],
};

export function applyFlowFilters(flows: InternalApiScreeningFlow[], filters: FlowFilters): InternalApiScreeningFlow[] {
  return flows
    .filter((flow) => !filters.screenableFields.length || filters.screenableFields.includes(flow.field))
    .filter(
      (flow) =>
        !filters.segments.length ||
        flow.includedSegments?.some((s) => filters.segments.some((sf) => sf.code === s)) ||
        flow.excludedSegments?.some((s) => filters.segments.some((sf) => sf.code === s))
    )
    .filter((flow) => !filters.matchingTypes.length || filters.matchingTypes.includes(flow.match.type))
    .filter(
      (flow) =>
        !filters.screenedAgainst.length ||
        flow.lists?.filter(({ list }) => !!list.name)?.some(({ list }) => filters.screenedAgainst.includes(list.name!))
    );
}

type Props = {
  flows: InternalApiScreeningFlow[];
  segments: InternalApiEntityGroup[];
  value: FlowFilters;
  onChange: (filters: FlowFilters) => void;
};

export const ScreeningFlowFilters: React.FC<Props> = ({ flows, segments, value, onChange }) => (
  <>
    <SalvDropdown
      label={'Screened fields'}
      options={getSreenedFieldsOptions(flows)}
      size={Size.SMALL}
      placeholder={'All fields'}
      isMulti
      value={value.screenableFields.map((f) => toDropdownOption(f)).filter(isDefined)}
      onChange={(values) => onChange({ ...value, screenableFields: values.map((v) => v.key) })}
    />
    <SalvDropdown
      label={'Segments'}
      options={getSegmentsOptions(segments)}
      size={Size.SMALL}
      placeholder={'All Segments'}
      isMulti
      value={value.segments.map((s) => toDropdownOption(s.name, s?.code)).filter(isDefined)}
      onChange={(values) =>
        onChange({ ...value, segments: values.map((v) => segments.find((g) => g.code === v.key)!) })
      }
    />
    <SalvDropdown
      label={'Matching type'}
      options={Object.values(InternalApiScreeningMatchType).map((val) => ({
        key: val,
        value: val,
        label: getMatchLabel(val),
      }))}
      size={Size.SMALL}
      isMulti
      placeholder={'All types'}
      value={value.matchingTypes.map((f) => toDropdownOption(f)).filter(isDefined)}
      onChange={(values) =>
        onChange({ ...value, matchingTypes: values.map((v) => v.key as InternalApiScreeningMatchType) })
      }
    />
    <SalvDropdown
      label={'Screened against'}
      options={getSreenedAgainstOptions(flows)}
      size={Size.SMALL}
      placeholder={'All fields'}
      isMulti
      value={value.screenedAgainst.map((f) => toDropdownOption(f)).filter(isDefined)}
      onChange={(values) => onChange({ ...value, screenedAgainst: values.map((v) => v.key) })}
    />
  </>
);
